import React, { useRef, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { CraftingRecipe, Resource } from '../../types';
import { Clock, ArrowRight } from 'lucide-react';
import { ItemIcon } from '../common/ItemIcon';
import { Button } from '../common/Button';

interface VirtualRecipeListProps {
    recipes: CraftingRecipe[];
    resources: Record<string, number>;
    craftingLevel: number;
    hasAvailableSlot: boolean;
    onCraft: (recipeId: string) => void;
    onDragStart: (e: React.DragEvent, recipeId: string) => void;
}

// Dynamic height estimation based on content
const getRecipeHeight = (recipe: CraftingRecipe) => {
    // Base height: Padding(24) + Header(32) + Margins(20) + Footer(47) + WrapperPadding(12) = ~135px
    // Ingredient row height: 24px
    const rows = Math.max(Object.keys(recipe.input).length, Object.keys(recipe.output).length);
    return 120 + (rows * 24);
};

const formatTime = (ms: number) => (ms / 1000).toFixed(1) + 's';

// Memoized recipe card to prevent unnecessary re-renders
const RecipeCard = React.memo<{
    recipe: CraftingRecipe;
    resources: Record<string, number>;
    canCraft: boolean;
    hasResources: boolean;
    hasAvailableSlot: boolean;
    onCraft: (recipeId: string) => void;
    onDragStart: (e: React.DragEvent, recipeId: string) => void;
}>(({ recipe, resources, canCraft, hasResources, hasAvailableSlot, onCraft, onDragStart }) => {
    return (
        <div
            draggable={canCraft}
            onDragStart={(e) => onDragStart(e, recipe.id)}
            style={{
                background: canCraft ? 'var(--color-bg-panel-hover)' : 'rgba(0,0,0,0.2)',
                border: `1px solid ${canCraft ? 'var(--color-border)' : 'transparent'}`,
                borderRadius: '8px',
                padding: '12px',
                opacity: canCraft ? 1 : 0.6,
                transition: 'all 0.2s',
                cursor: canCraft ? 'grab' : 'default',
                minHeight: '128px', // Base height
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{recipe.name}</span>
                <span style={{
                    fontSize: '0.75rem',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: canCraft ? 'rgba(255,255,255,0.1)' : 'rgba(239, 68, 68, 0.2)',
                    color: canCraft ? 'var(--color-text-secondary)' : 'var(--color-danger)'
                }}>
                    Lvl {recipe.level}
                </span>
            </div>

            <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '12px', flex: 1 }}>
                <div>
                    {Object.entries(recipe.input).map(([res, amt]) => (
                        <div key={res} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: (resources[res as Resource] ?? 0) < (amt as number) ? 'var(--color-danger)' : 'inherit' }}>
                            <ItemIcon id={res} size={16} />
                            {amt}x {res.replace('_', ' ')}
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--color-text-muted)' }}><ArrowRight size={14} /></div>
                <div>
                    {Object.entries(recipe.output).map(([res, amt]) => (
                        <div key={res} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-success)' }}>
                            <ItemIcon id={res} size={16} />
                            {amt}x {res.replace('_', ' ')}
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                    <Clock size={14} /> {formatTime(recipe.duration)}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        size="sm"
                        variant={hasAvailableSlot ? 'primary' : 'outline'}
                        disabled={!canCraft || !hasResources || !hasAvailableSlot}
                        onClick={() => onCraft(recipe.id)}
                    >
                        {hasAvailableSlot ? 'Craft Once' : 'Slots Busy'}
                    </Button>
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison - only re-render when relevant props change
    if (prevProps.recipe.id !== nextProps.recipe.id) return false;
    if (prevProps.canCraft !== nextProps.canCraft) return false;
    if (prevProps.hasResources !== nextProps.hasResources) return false;
    if (prevProps.hasAvailableSlot !== nextProps.hasAvailableSlot) return false;
    return true;
});

RecipeCard.displayName = 'RecipeCard';

export const VirtualRecipeList: React.FC<VirtualRecipeListProps> = ({
    recipes,
    resources,
    craftingLevel,
    hasAvailableSlot,
    onCraft,
    onDragStart,
}) => {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: recipes.length,
        getScrollElement: () => parentRef.current,
        estimateSize: (i) => getRecipeHeight(recipes[i]!),
        overscan: 5, // Render 5 extra items above/below viewport
    });

    const items = virtualizer.getVirtualItems();

    return (
        <div
            ref={parentRef}
            style={{
                height: '100%',
                overflow: 'auto',
            }}
        >
            <div
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {items.map((virtualItem) => {
                    const recipe = recipes[virtualItem.index];
                    if (!recipe) return null;

                    const canCraft = craftingLevel >= recipe.level;
                    const hasResources = Object.entries(recipe.input).every(
                        ([res, amt]) => (resources[res as Resource] ?? 0) >= (amt as number)
                    );

                    return (
                        <div
                            key={recipe.id}
                            data-index={virtualItem.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualItem.start}px)`,
                                padding: '6px 0',
                                boxSizing: 'border-box',
                            }}
                        >
                            <RecipeCard
                                recipe={recipe}
                                resources={resources}
                                canCraft={canCraft}
                                hasResources={hasResources}
                                hasAvailableSlot={hasAvailableSlot}
                                onCraft={onCraft}
                                onDragStart={onDragStart}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
